import {
    CountMetric,
    LatencyMetric,
    EnvironmentDetail,
    globalEventBus,
    UnsupportedEnvironmentException, VoiceNotEnabledException, InvalidInputException, WakeWordTimeoutException,
    PromiseUtils,
} from '@amzn/ihm-associate-tools-js-lib'
import { SeawardEventBusTopicsConstants } from '@utils/Constants/SeawardEventBusTopicsConstants'
import * as CandyCaneService from '@services/CandyCaneService'
import { CandyCaneWakeWordErrorMappings } from '@services/voice/CandyCaneWakeWordErrorMappings'
import * as VoiceUtilsService from '@utils/VoiceUtilsService'

const WakeWordConstants = Object.freeze({
    DEFAULT_TIMEOUT: 600000, // 10 minutes in ms
    MAX_TIMEOUT: 1800000, // 30 minutes in ms
})

const programName = 'IhmFlyingDutchman'

export class CandyCaneWakeWordService {

    public static wakeWordWarmedUp = false

    // public static functions

    /**
     * Proxy function for initialization so that it keeps consistent naming of Voice.
     *
     * @return {Promise} Resolves if the initialization of WakeWord was successful and fails otherwise.
     */
    public static warmup () {

        if (!EnvironmentDetail.isAndroid()) {
            console.error('CandyCaneWakeWordService is only supported on Android')
            return Promise.reject(new UnsupportedEnvironmentException({
                message: 'CandyCaneWakeWordService is only supported on Android.',
            }))
        }

        const warmupFaultMetrics = new CountMetric(undefined, 'Fault', programName, 'candyCaneWakeWordService.warmup')
        const warmupLatencyMetrics = new LatencyMetric(undefined, 'Time', programName, 'candyCaneWakeWordService.warmup')
        warmupLatencyMetrics.start()

        return VoiceUtilsService.verifyVoiceIsEnabled()
            .then(CandyCaneWakeWordService.initialize)
            .then(() => {
                warmupFaultMetrics.count(0)
                CandyCaneWakeWordService.wakeWordWarmedUp = true
            })
            .catch((error) => {
                console.error('candyCaneWakeWordService warmup() error ', error)
                return CandyCaneWakeWordService.handleError(error, warmupFaultMetrics)
            })
            .finally(() => {
                warmupLatencyMetrics.stop()
            })
    }

    /**
     * Manages start and stop of the service so that wake word detection is handled.
     * The purpose of this method is to prevent the situation where stop is not called
     * after the call to start, because stop must be called every time when the start is called
     * no matter if the wake word is detected or not.
     * Listening to wake word will time out after a given timeout in config.
     * Default timeout is 10 minutes, and the maximum is 30 minutes.
     *
     * config parameter includes following items,
     * timeout: wake word listening timeout in ms.
     *
     * @return {Promise} Resolves if the wake word detection was successful and fails otherwise.
     */
    public static listen (config) {

        if (!EnvironmentDetail.isAndroid()) {
            console.error('CandyCaneWakeWordService is only supported on Android')
            return Promise.reject(new UnsupportedEnvironmentException({
                message: 'CandyCaneWakeWordService is only supported on Android.',
            }))
        }

        config = config || {}
        config.timeout = config.timeout || WakeWordConstants.DEFAULT_TIMEOUT

        const listenFaultMetrics = new CountMetric(undefined, 'Fault', programName, 'candyCaneWakeWordService.listen')
        const listenLatencyMetrics = new LatencyMetric(undefined, 'Time', programName, 'candyCaneWakeWordService.listen')
        listenLatencyMetrics.start()

        if (config.timeout > WakeWordConstants.MAX_TIMEOUT) {
            console.warn('Given timeout value ', config.timeout.toString(), 'ms is greater than the maximum timeout')
            listenFaultMetrics.count(1)
            listenLatencyMetrics.stop()
            return Promise.reject(new InvalidInputException({
                message: 'Given timeout value is greater than maximum value',
            }))
        }

        const listenPromise = VoiceUtilsService.verifyVoiceIsEnabled()
            .then(CandyCaneWakeWordService.start)
            .then(CandyCaneWakeWordService.stop)
            .then(() => {
                listenFaultMetrics.count(0)
            })
        return PromiseUtils.executeWithTimeout(listenPromise, {
            durationMillis: config.timeout,
            error: new WakeWordTimeoutException({
                message: 'Listening to wake word timed out',
            }),
        })
            .catch((error) => {
                if (error instanceof WakeWordTimeoutException) {
                    console.warn('candyCaneWakeWordService listen() timed out')
                    CandyCaneWakeWordService.cancel()
                    return Promise.reject(error)
                }
                console.error('candyCaneWakeWordService listen() error ', error)
                return CandyCaneWakeWordService.handleError(error, listenFaultMetrics)
            })
            .finally(() => {
                listenLatencyMetrics.stop()
            })
    }

    /*
     * Proxy function for cancelling the wake word listening.
     *
     * @return {Promise} Resolves if the stop of WakeWord was successful and fails otherwise.
     */
    public static cancel () {

        if (!EnvironmentDetail.isAndroid()) {
            console.error('CandyCaneWakeWordService is only supported on Android')
            return Promise.reject(new UnsupportedEnvironmentException({
                message: 'CandyCaneWakeWordService is only supported on Android.',
            }))
        }

        const cancelFaultMetrics = new CountMetric(undefined, 'Fault', programName, 'candyCaneWakeWordService.cancel')
        const cancelLatencyMetrics = new LatencyMetric(undefined, 'Time', programName, 'candyCaneWakeWordService.cancel')
        cancelLatencyMetrics.start()

        return VoiceUtilsService.verifyVoiceIsEnabled()
            .then(CandyCaneWakeWordService.stop)
            .then(() => {
                cancelFaultMetrics.count(0)
            })
            .catch((error) => {
                console.error('candyCaneWakeWordService cancel() error', error)
                return CandyCaneWakeWordService.handleError(error, cancelFaultMetrics)
            })
            .finally(() => {
                cancelLatencyMetrics.stop()
            })
    }

    /**
     * Registers a listener with the EventBus to get notified when User logs out or changes tool within CandyCane
     */
    public static activate () {
        globalEventBus.on(SeawardEventBusTopicsConstants.LOGOUT, 'candyCaneWakeWordService', CandyCaneWakeWordService.cancelIfWarmedUp)
        globalEventBus.on(SeawardEventBusTopicsConstants.TOOL_SWITCH, 'candyCaneWakeWordService', CandyCaneWakeWordService.cancelIfWarmedUp)
    }

    // private static functions

    /**
     * Initializes the service so that WakeWordDetector is setup for the wake word detection.
     * WakeWordDetector must be initialized before the detection, otherwise it will fail.
     *
     * @return {Promise} Resolves if the initialize was successful and fails otherwise.
     */
    private static initialize () {
        return CandyCaneService.wakeWordInitialize()
    }

    /**
     * Starts the service for the wake word detection.
     * Promise is resolved when wake word is detected, but if listening is stopped
     * in the middle of detection, it will be rejected.
     *
     * @return {Promise} Resolves if the start was successful and fails otherwise.
     */
    private static start () {
        const startLatencyMetrics = new LatencyMetric(undefined, 'Time', programName, 'candyCaneWakeWordService.start')
        startLatencyMetrics.start()
        const startPromise = CandyCaneService.wakeWordStart()
        startLatencyMetrics.stop()
        return startPromise
    }

    /**
     * Stops the service so that wake word detection is stopped.
     * If there is a Promise that is waiting for the wake word, it will be rejected.
     * WakeWordDetector must be initialized before the detection, otherwise it will fail.
     *
     * @return {Promise} Resolves if the stop was successful and fails otherwise.
     */
    private static stop () {
        return CandyCaneService.wakeWordStop()
    }

    /**
     * Handles error that is returned by the Weblab check.
     *
     * @return {Promise} Rejected with corresponding Exception to the error.
     */
    private static handleError (error, faultMetric) {
        if (error instanceof VoiceNotEnabledException) {
            faultMetric.count(0)
            return Promise.reject(error)
        }

        faultMetric.count(1)
        new CountMetric(undefined, CandyCaneWakeWordErrorMappings.getMetricNameForError(error), programName, 'candyCaneWakeWordService.errors').count(1)
        return Promise.reject(CandyCaneWakeWordErrorMappings.getExceptionForError(error))
    }

    /**
     * Cancels only if warmed up
     */
    private static cancelIfWarmedUp () {
        if (CandyCaneWakeWordService.wakeWordWarmedUp) {
            CandyCaneWakeWordService.cancel()
        }
    }
}
